/**
 * Will this submission be painted onto a real photograph, or generated?
 *
 * The visualizer route segments a surface in an actual picture and maps the
 * vendor's seamless onto the plane it finds. Walls are the interesting case:
 * a room has several, and "walls" as an input does not say which. So that
 * route runs in two passes — analyse the picture, let the vendor pick the
 * walls they meant, then texture only those — and the canvas shows a
 * WallPicker node between them.
 *
 * Both passes have been in the worker all along. What stopped happening is
 * the frontend asking for the first one: the studio was made mandatory
 * wherever it existed, `use3dCamera` was force-set on every From Reference
 * node, and this routing tested that flag. So every wall in the picture got
 * textured, because nobody was ever asked which.
 *
 * The flag was never the right question. What decides is which route the
 * vendor asked for — the same thing `matchStrength` decides everywhere else.
 *
 * MIRRORS backoffice-worker/src/content-studio/from-reference-route.ts. The
 * two repos cannot share code, so they share cases instead: the tests on both
 * sides encode the same table, and a change to one is a change to both.
 */

export interface VisualizerRouteInput {
    /** Already resolved through effectiveRecipeSlug. */
    recipeSlug?: string;
    visualizationType?: string;
    inputs?: Record<string, any> | null;
}

const VISUALIZER_RECIPES = new Set(['lifestyle-scene', 'lifestyle-floor', 'from-reference']);

/** Does this job paint onto a photograph rather than generate a scene? */
export function routesThroughVisualizer(job: VisualizerRouteInput): boolean {
    if (!job.recipeSlug || !VISUALIZER_RECIPES.has(job.recipeSlug)) return false;
    // The route maps a material onto a plane, which needs a material.
    if (job.visualizationType !== 'surface') return false;

    if (job.recipeSlug !== 'from-reference') return true;

    const inputs = job.inputs || {};
    // 'exact' is the recipe's default: the vendor's own photograph with a new
    // surface in it. Only a capture the vendor actually asked for routes away.
    const strength = String(inputs.matchStrength || 'exact');
    // Same-structure maps the surface first too — it just generates a room
    // around it afterwards instead of polishing what is there.
    if (strength === 'exact' || strength === 'inspired') return true;
    const studioOn = inputs.use3dCamera === true || inputs.use3dCamera === 'true';
    return !(studioOn && !!inputs.snapshotUrl);
}

/**
 * Should the vendor be asked which walls first?
 *
 * Only where there is a real picture to point at and more than one wall could
 * be meant. A floor is one plane, so it needs no picking; the 3D studio has
 * its own wall toggles, so a posed shot needs none either.
 */
export function needsWallPicker(job: VisualizerRouteInput): boolean {
    const surface = String((job.inputs || {}).surface || '');
    return surface === 'walls' && routesThroughVisualizer(job);
}

/** Does this route keep the vendor's photograph as it is? */
export function keepsThePhotograph(job: VisualizerRouteInput): boolean {
    if (job.recipeSlug !== 'from-reference') return false;
    return String((job.inputs || {}).matchStrength || 'exact') === 'exact';
}
